import * as AppConstants from 'app/AppConstants';
import keyBy from 'lodash/keyBy';
import cloneDeep from 'lodash/cloneDeep';
import pull from 'lodash/pull';
import {BUZZ_MESSAGE_TYPES} from 'constants/BuzzConstants';
import {ATTACHMENT_TYPES, TAG_TYPES} from 'app/MessageConstants';
import {concat, difference, intersect} from 'utilities/Set';
import reduce from 'lodash/reduce';
import omit from 'lodash/omit';
import flatMap from 'lodash/flatMap';

const initialPermissionsState = () => ({
  groupIds: new Set(),
  userIds: new Set(),
});

const initialState = () => ({
  failedMessageIds: new Set(),
  retryMessages: {},
  byChannelId: {},
  messages: {},
  favoritedIds: new Set(),
  author: {
    focus: false,
    target: {},
    mentionedWithoutPermissions: initialPermissionsState(),
    caretPosition: 0,
    tagPosition: null,
    content: {text: '', tags: []},
    attachmentIds: new Set(),
    attachments: {},
    isCreating: false,
    quoteId: null,
  },
});

const propertyKey = userId => `USER:${userId}`;

export default (state = initialState(), action) => {
  switch (action.type) {
    case AppConstants.BUZZ__RESET_CHANNEL: {
      return {
        ...state,
        byChannelId: {
          ...state.byChannelId,
          [action.payload.channelId]: new Set(),
        },
      };
    }

    case AppConstants.BUZZ__MESSAGE_LOADED: {
      const message = action.payload.message;
      return {
        ...state,
        messages: {
          ...state.messages,
          [message.id]: message,
        },
        byChannelId: {
          ...state.byChannelId,
          [message.channelId]: new Set([
            ...(state.byChannelId[message.channelId] || []),
            message.id,
          ]),
        },
      };
    }

    case AppConstants.BUZZ__LOADED_PINNED_CHANNELS:
    case AppConstants.BUZZ__LOADED_RECENT_ACTIVITY_TOPIC_CHANNELS: {
      const messages = flatMap(
        action.payload.channels,
        channel =>
          channel.messages &&
          channel.messages.map(message => ({
            ...state.messages[message.id],
            ...message,
            channelId: channel.id,
          }))
      ).filter(x => x);
      const messagesById = keyBy(messages, 'id');
      const propId = message => message.id;
      const byChannelId = action.payload.channels.reduce((memo, channel) => {
        if (!channel.messages) return memo;
        const messageIds = channel.messages.map(propId);
        return {
          ...memo,
          [channel.id]: new Set([
            ...(state.byChannelId[channel.id] || []),
            ...messageIds,
          ]),
        };
      }, {});

      return {
        ...state,
        byChannelId: {
          ...state.byChannelId,
          ...byChannelId,
        },
        messages: {
          ...state.messages,
          ...messagesById,
        },
      };
    }

    case AppConstants.BUZZ__FLAT_MESSAGES_LOADED: {
      const {messages} = action.payload;
      const nestedMessages = flatMap(
        messages,
        message =>
          message.messages
            ? message.messages.map(m => ({channelId: message.id, ...m}))
            : null
      ).filter(Boolean);
      const flatMessages = [...messages, ...nestedMessages];
      const keyedMessages = keyBy(flatMessages, 'id');
      const byChannelId = flatMessages.reduce((memo, message) => {
        memo[message.channelId] =
          memo[message.channelId] ||
          new Set(state.byChannelId[message.channelId]);
        memo[message.channelId].add(message.id);
        return memo;
      }, {});

      return {
        ...state,
        byChannelId: {
          ...state.byChannelId,
          ...byChannelId,
        },
        messages: {...state.messages, ...keyedMessages},
      };
    }

    case AppConstants.BUZZ__MENTIONED_WITHOUT_PERMISSIONS: {
      return {
        ...state,
        author: {
          ...state.author,
          mentionedWithoutPermissions: {
            userIds: new Set(action.payload.userIds),
            groupIds: new Set(action.payload.groupIds),
          },
        },
      };
    }

    case AppConstants.BUZZ__MESSAGE_RESET_PERMISSIONS: {
      return {
        ...state,
        author: {
          ...state.author,
          mentionedWithoutPermissions: initialPermissionsState(),
        },
      };
    }

    case AppConstants.BUZZ__MESSAGE_CREATED_BY_SERVER: {
      const next = new Set(
        state.byChannelId[action.payload.message.channelId]
      ).add(action.payload.message.id);
      return {
        ...state,
        byChannelId: {
          ...state.byChannelId,
          [action.payload.message.channelId]: next,
        },
        messages: {
          ...state.messages,
          [action.payload.message.id]: action.payload.message,
        },
      };
    }

    case AppConstants.BUZZ__HUDDLE_MESSAGE_CREATED_BY_SERVER: {
      const nextChannelIds = new Set(
        state.byChannelId[action.payload.message.channelId]
      ).add(action.payload.message.id);
      return {
        ...state,
        byChannelId: {
          ...state.byChannelId,
          [action.payload.message.channelId]: nextChannelIds,
        },
        messages: {
          ...state.messages,
          [action.payload.message.id]: action.payload.message,
        },
      };
    }

    case AppConstants.BUZZ__USER_CREATE_MESSAGE_STARTED: {
      const next = new Set(
        state.byChannelId[action.payload.message.channelId]
      ).add(action.payload.message.id);
      return {
        ...state,
        byChannelId: {
          ...state.byChannelId,
          [action.payload.message.channelId]: next,
        },
        messages: {
          ...state.messages,
          [action.payload.message.id]: action.payload.message,
        },
        retryMessages: {
          ...state.retryMessages,
          [action.payload.message.id]: action.payload.request,
        },
        author: {
          ...initialState().author,
          isCreating: !action.payload.isRetry,
        },
      };
    }

    case AppConstants.BUZZ__DELETE_MESSAGE_STARTED: {
      const messageId = action.payload.messageId;
      const message = state.messages[messageId];
      const DELETED_TEXT = 'This message has been deleted.';
      return {
        ...state,
        messages: {
          ...state.messages,
          [messageId]: {
            ...message,
            messageType: BUZZ_MESSAGE_TYPES.SYSTEM,
            content: {text: DELETED_TEXT},
            attachments: [],
            oldMessage: message,
          },
        },
      };
    }

    case AppConstants.BUZZ__DELETE_MESSAGE_FAILED: {
      const messageId = action.payload.messageId;
      const message = state.messages[messageId];
      const oldMessage = message.oldMessage;
      return {
        ...state,
        messages: {
          ...state.messages,
          [messageId]: {
            ...oldMessage,
          },
        },
      };
    }

    case AppConstants.BUZZ__USER_CREATE_MESSAGE_FINISHED: {
      const next = difference(
        new Set(state.byChannelId[action.payload.message.channelId]).add(
          action.payload.message.id
        ),
        [action.payload.messageId]
      );

      const messages = reduce(
        state.messages,
        (memo, message) => {
          if (message.id === action.payload.messageId) return memo;
          memo[message.id] = message;
          return memo;
        },
        {[action.payload.message.id]: action.payload.message}
      );

      const retryMessages = omit(state.retryMessages, action.payload.messageId);

      return {
        ...state,
        byChannelId: {
          ...state.byChannelId,
          [action.payload.message.channelId]: next,
        },
        retryMessages,
        messages,
        author: {
          ...state.author,
          isCreating: false,
        },
      };
    }

    case AppConstants.BUZZ__USER_CREATE_MESSAGE_FAILED: {
      return {
        ...state,
        failedMessageIds: new Set(state.failedMessageIds).add(
          action.payload.messageId
        ),
        author: initialState().author,
      };
    }

    case AppConstants.BUZZ__USER_CREATE_MESSAGE_ABANDONED: {
      const messages = omit(state.messages, action.payload.messageId);
      const retryMessages = omit(state.retryMessages, action.payload.messageId);

      const byChannelId = difference(
        new Set(state.byChannelId[action.payload.channelId]),
        [action.payload.messageId]
      );

      const failedMessageIds = difference(new Set(state.failedMessageIds), [
        action.payload.messageId,
      ]);

      return {
        ...state,
        messages,
        retryMessages,
        failedMessageIds,
        byChannelId: {
          ...state.byChannelId,
          [action.payload.channelId]: byChannelId,
        },
      };
    }

    case AppConstants.BUZZ__ADDED_ATTACHMENT_STARTED: {
      const attachment = {
        id: action.payload.id,
        type: ATTACHMENT_TYPES.LOADING,
      };
      const attachmentIds = new Set(state.author.attachmentIds).add(
        attachment.id
      );
      const attachments = {
        ...state.author.attachments,
        [attachment.id]: attachment,
      };
      return {...state, author: {...state.author, attachmentIds, attachments}};
    }

    case AppConstants.BUZZ__ADDED_ATTACHMENT_FINISHED: {
      const attachment = action.payload;
      const attachmentIds = new Set(state.author.attachmentIds).add(
        attachment.id
      );
      const attachments = {
        ...state.author.attachments,
        [attachment.id]: attachment,
      };
      return {...state, author: {...state.author, attachmentIds, attachments}};
    }

    case AppConstants.BUZZ__REMOVED_ATTACHMENT:
    case AppConstants.BUZZ__ADDED_ATTACHMENT_FAILED: {
      const attachmentIds = new Set(state.author.attachmentIds);
      attachmentIds.delete(action.payload.id);
      const attachments = {...state.author.attachments};
      delete attachments[action.payload.id];

      return {...state, author: {...state.author, attachmentIds, attachments}};
    }

    case AppConstants.BUZZ__MESSAGE_TEXT_UPDATED: {
      const idsByCategory = action.payload.content.tags.reduce(
        (memo, tag) => {
          if (tag.type === TAG_TYPES.GROUP) {
            memo.groupIds = memo.groupIds.concat([parseInt(tag.id, 10)]);
          } else if (tag.type === TAG_TYPES.USER) {
            memo.userIds = memo.userIds.concat([parseInt(tag.id, 10)]);
          }
          return memo;
        },
        {userIds: [], groupIds: []}
      );

      return {
        ...state,
        author: {
          ...state.author,
          caretPosition: action.payload.caretPosition,
          content: action.payload.content,
          mentionedWithoutPermissions: {
            userIds: intersect(
              state.author.mentionedWithoutPermissions.userIds,
              idsByCategory.userIds
            ),
            groupIds: intersect(
              state.author.mentionedWithoutPermissions.groupIds,
              idsByCategory.groupIds
            ),
          },
        },
      };
    }

    case AppConstants.BUZZ__MESSAGE_MENTION_STARTED: {
      return {
        ...state,
        author: {
          ...state.author,
          caretPosition: action.payload.caretPosition,
          tagPosition: action.payload.caretPosition,
        },
      };
    }

    case AppConstants.BUZZ__MESSAGE_MENTION_CANCELLED: {
      return {
        ...state,
        author: {
          ...state.author,
          tagPosition: null,
          tagSearchResults: null,
        },
      };
    }

    case AppConstants.BUZZ__MESSAGE_MENTION_SEARCH_LOADED: {
      return {
        ...state,
        author: {
          ...state.author,
          tagSearchResults: action.payload.results,
        },
      };
    }

    case AppConstants.BUZZ__AUTHOR_OBJECT_TARGETED: {
      return {
        ...state,
        author: {
          ...state.author,
          target: action.payload,
        },
      };
    }

    case AppConstants.BUZZ__MESSAGE_TAG_ADDED: {
      return {
        ...state,
        author: {
          ...state.author,
          tagPosition: null,
          tagSearchResults: null,
          content: {
            text: action.payload.text,
            tags: [...state.author.content.tags, action.payload.tag],
          },
          caretPosition: action.payload.caretPosition,
        },
      };
    }

    case AppConstants.BUZZ__FAVORITES_LOADED: {
      return {
        ...state,
        favoritedIds: concat(state.favoritedIds, action.payload.messageIds),
      };
    }

    case AppConstants.BUZZ__FAVORITE_MESSAGE_STARTED: {
      return {
        ...state,
        favoritedIds: concat(state.favoritedIds, [action.payload.messageId]),
      };
    }

    case AppConstants.BUZZ__FAVORITE_MESSAGE_FAILED: {
      return {
        ...state,
        favoritedIds: difference(state.favoritedIds, [
          action.payload.messageId,
        ]),
      };
    }

    case AppConstants.BUZZ__UNFAVORITE_MESSAGE_STARTED: {
      return {
        ...state,
        favoritedIds: difference(state.favoritedIds, [
          action.payload.messageId,
        ]),
      };
    }

    case AppConstants.BUZZ__UNFAVORITE_MESSAGE_FAILED: {
      return {
        ...state,
        favoritedIds: concat(state.favoritedIds, [action.payload.messageId]),
      };
    }

    case AppConstants.BUZZ__MESSAGE_REACTION_ADDED: {
      const {userId, messageId, reaction} = action.payload;
      const message = state.messages[messageId];
      const reactions = cloneDeep(message.reactions) || [];
      let targetReaction = reactions.find(r => r.reactionKey === reaction);
      if (!targetReaction) {
        targetReaction = {reactionKey: reaction, propertyKeys: []};
        reactions.push(targetReaction);
      }
      targetReaction.propertyKeys.push(propertyKey(userId));

      return {
        ...state,
        messages: {
          ...state.messages,
          [messageId]: {
            ...message,
            reactions,
          },
        },
      };
    }

    case AppConstants.BUZZ__MESSAGE_REACTION_REMOVED: {
      const {userId, messageId, reaction} = action.payload;
      const message = state.messages[messageId];
      const reactions = cloneDeep(message.reactions) || [];
      const targetReaction = reactions.find(r => r.reactionKey == reaction);
      if (targetReaction) {
        pull(targetReaction.propertyKeys, propertyKey(userId));
        if (targetReaction.propertyKeys.length === 0) {
          pull(reactions, targetReaction);
        }
      }

      return {
        ...state,
        messages: {
          ...state.messages,
          [messageId]: {
            ...message,
            reactions,
          },
        },
      };
    }

    case AppConstants.BUZZ__ADDED_QUOTE_ATTACHMENT: {
      return {
        ...state,
        author: {
          ...state.author,
          focus: true,
          quoteId: action.payload.quoteId,
        },
      };
    }

    case AppConstants.BUZZ__REMOVED_QUOTE_ATTACHMENT: {
      return {
        ...state,
        author: {
          ...state.author,
          quoteId: null,
        },
      };
    }

    case AppConstants.BUZZ__MESSAGE_BLURRED: {
      return {
        ...state,
        author: {
          ...state.author,
          focus: false,
        },
      };
    }

    default:
      return state;
  }
};
